import React, { useState } from "react";
import { Button, Col, FormGroup, Input, Label, Row, Spinner, Table } from "reactstrap";
import moment from "moment";

import TablePagination from "../../../components/TablePagination";
import { listarNotasDisponiveis, vincularNotas } from "../../../services/romaneioManualApi";
import {
  Aviso,
  CheckboxTodos,
  EstadoTabela,
  ModalHub,
  SelecaoResumo,
  extractError,
  fieldInputStyle,
  fmtBRL,
  fmtData,
  fmtNumber,
  fmtPeso,
  useSelecao,
} from "../_documentosFrete/comum";

const PAGE_SIZES = [20, 50, 100];
const FILTRO_VAZIO = { numero: "", pedido: "", chave: "", de: "", ate: "" };
export const MAX_DIAS_EMISSAO = 365;

// Pesquisa de notas para vincular ao romaneio (legado: romaneio/pesquisa). Só
// aparecem notas sem romaneio da transportadora do romaneio; exige ao menos um
// filtro (número, pedido, chave ou emissão de + até, no máximo 365 dias).
export function validarFiltroNotas(f) {
  const temPeriodo = Boolean(f.de || f.ate);
  if (temPeriodo && (!f.de || !f.ate)) return "Informe a emissão de e até.";
  if (f.de && f.ate && f.de > f.ate) return "A emissão inicial é maior que a final.";
  if (f.de && f.ate && moment(f.ate, "YYYY-MM-DD").diff(moment(f.de, "YYYY-MM-DD"), "days") > MAX_DIAS_EMISSAO) {
    return `O período de emissão é de no máximo ${MAX_DIAS_EMISSAO} dias.`;
  }
  if (f.numero.trim() && !/^\d+$/.test(f.numero.trim())) return "O número da NF aceita só dígitos.";
  if (!f.numero.trim() && !f.pedido.trim() && !f.chave.trim() && !temPeriodo) {
    return "Informe ao menos um filtro: número da NF, pedido, chave de acesso ou emissão de/até.";
  }
  return "";
}

export default function PesquisaNotasModal({ romaneio, onClose, onVinculado }) {
  const [draft, setDraft] = useState(FILTRO_VAZIO);
  const [filtro, setFiltro] = useState(null); // aplicado
  const [erroFiltro, setErroFiltro] = useState("");
  const [dados, setDados] = useState({ total: 0, itens: [] });
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [vinculando, setVinculando] = useState(false);
  const [erroVincular, setErroVincular] = useState("");
  const selecao = useSelecao();

  const consultar = async (f, pagina, tamanho) => {
    setLoading(true);
    setErro("");
    try {
      const params = { page: pagina, pageSize: tamanho };
      if (f.numero.trim()) params.numero = f.numero.trim();
      if (f.pedido.trim()) params.pedido = f.pedido.trim();
      if (f.chave.trim()) params.chave = f.chave.trim();
      if (f.de && f.ate) {
        params.de = f.de;
        params.ate = f.ate;
      }
      const r = await listarNotasDisponiveis(romaneio.id, params);
      setDados({ total: Number(r.total) || 0, itens: r.itens || [] });
    } catch (e) {
      setDados({ total: 0, itens: [] });
      setErro(extractError(e, "Não foi possível pesquisar as notas."));
    } finally {
      setLoading(false);
    }
  };

  const pesquisar = () => {
    const msg = validarFiltroNotas(draft);
    setErroFiltro(msg);
    if (msg) return;
    const f = { ...draft };
    setFiltro(f);
    setPage(1);
    selecao.limpar();
    consultar(f, 1, pageSize);
  };

  const mudarPagina = (pagina, tamanho = pageSize) => {
    setPage(pagina);
    setPageSize(tamanho);
    if (filtro) consultar(filtro, pagina, tamanho);
  };

  const vincular = async () => {
    if (vinculando || !selecao.ids.length) return;
    setVinculando(true);
    setErroVincular("");
    try {
      const r = await vincularNotas(romaneio.id, selecao.ids);
      onVinculado({ vinculadas: Number(r.vinculadas) || 0, ignoradas: Array.isArray(r.ignoradas) ? r.ignoradas : [] });
    } catch (e) {
      setErroVincular(extractError(e, "Não foi possível vincular as notas."));
      setVinculando(false);
    }
  };

  const set = (campo, valor) => setDraft((atual) => ({ ...atual, [campo]: valor }));
  const aoTeclar = (e) => {
    if (e.key === "Enter") pesquisar();
  };

  return (
    <ModalHub titulo={`Adicionar notas ao romaneio ${romaneio.codigo || `#${romaneio.id}`}`} icone="tim-icons icon-zoom-split" onClose={() => !vinculando && onClose()} maxWidth={980}>
      <div className="docs-frete__form">
        <Row>
          <Col md="2">
            <FormGroup>
              <Label for="pesquisa-numero">Número NF</Label>
              <Input id="pesquisa-numero" type="text" inputMode="numeric" value={draft.numero} onChange={(e) => set("numero", e.target.value)} onKeyDown={aoTeclar} style={fieldInputStyle} />
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label for="pesquisa-pedido">Pedido</Label>
              <Input id="pesquisa-pedido" type="text" value={draft.pedido} onChange={(e) => set("pedido", e.target.value)} onKeyDown={aoTeclar} style={fieldInputStyle} />
            </FormGroup>
          </Col>
          <Col md="4">
            <FormGroup>
              <Label for="pesquisa-chave">Chave de acesso</Label>
              <Input id="pesquisa-chave" type="text" maxLength={44} value={draft.chave} onChange={(e) => set("chave", e.target.value)} onKeyDown={aoTeclar} style={fieldInputStyle} />
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label for="pesquisa-de">Emissão de</Label>
              <Input id="pesquisa-de" type="date" value={draft.de} onChange={(e) => set("de", e.target.value)} style={fieldInputStyle} />
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label for="pesquisa-ate">Emissão até</Label>
              <Input id="pesquisa-ate" type="date" value={draft.ate} onChange={(e) => set("ate", e.target.value)} style={fieldInputStyle} />
            </FormGroup>
          </Col>
        </Row>
        <div className="docs-frete__barra">
          <span className="docs-frete__dica">Aparecem só notas sem romaneio da transportadora do romaneio.</span>
          <Button color="primary" size="sm" onClick={pesquisar} disabled={loading}>
            {loading ? <Spinner size="sm" style={{ marginRight: 6 }} /> : null}
            Pesquisar
          </Button>
        </div>
        {erroFiltro ? <Aviso tom="erro">{erroFiltro}</Aviso> : null}
      </div>

      <Aviso tom="info">Ao vincular, o frete simulado da nota é zerado e recalculado pelo romaneio.</Aviso>

      {filtro ? (
        <>
          <p className="docs-frete__secao">Notas encontradas · {fmtNumber(dados.total)}</p>
          {selecao.totais.qtd > 0 ? (
            <SelecaoResumo totais={selecao.totais}>
              <Button color="default" size="sm" outline onClick={selecao.limpar}>
                Desmarcar seleção
              </Button>
            </SelecaoResumo>
          ) : null}
          <EstadoTabela
            loading={loading}
            erro={erro}
            vazio={!loading && !dados.itens.length}
            textoCarregando="Pesquisando notas..."
            textoVazio="Nenhuma nota sem romaneio encontrada para este filtro."
          />
          {!loading && !erro && dados.itens.length ? (
            <>
              <Table className="docs-frete__tabela hub-thead-accent" size="sm" responsive hover>
                <thead className="text-primary">
                  <tr>
                    <th className="is-sel">
                      <CheckboxTodos itens={dados.itens} selecao={selecao} label="Selecionar todas as notas encontradas" />
                    </th>
                    <th>Nota</th>
                    <th>Chave</th>
                    <th>Emissão</th>
                    <th>Pedido</th>
                    <th>Destinatário</th>
                    <th>Cidade/UF</th>
                    <th className="is-number">Peso</th>
                    <th className="is-number">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {dados.itens.map((n) => (
                    <tr key={n.id}>
                      <td className="is-sel">
                        <input type="checkbox" aria-label={`Selecionar nota ${n.numero}`} checked={selecao.has(n.id)} onChange={() => selecao.toggle(n)} />
                      </td>
                      <td>
                        <strong>{n.numero}</strong>
                        <small>Série {n.serie || "-"}</small>
                      </td>
                      <td className="docs-frete__chave">{n.chave || "-"}</td>
                      <td className="docs-frete__nowrap">{fmtData(n.datEmissao)}</td>
                      <td>{n.pedido || "-"}</td>
                      <td>{n.destinatario || "-"}</td>
                      <td>{[n.cidadeDestino, n.ufDestino].filter(Boolean).join("/") || "-"}</td>
                      <td className="is-number">{fmtPeso(n.pesoBruto)}</td>
                      <td className="is-number">{fmtBRL(n.valorTotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <TablePagination
                page={page}
                pageSize={pageSize}
                total={dados.total}
                pageSizes={PAGE_SIZES}
                itemLabel="nota(s)"
                onPageChange={(p) => mudarPagina(p)}
                onPageSizeChange={(novo) => mudarPagina(1, novo)}
              />
            </>
          ) : null}
        </>
      ) : null}

      {erroVincular ? <Aviso tom="erro">{erroVincular}</Aviso> : null}

      <div className="docs-frete__modal-rodape">
        <Button color="secondary" size="sm" onClick={onClose} disabled={vinculando}>
          Cancelar
        </Button>
        <Button color="primary" size="sm" onClick={vincular} disabled={vinculando || !selecao.ids.length}>
          {vinculando ? <Spinner size="sm" style={{ marginRight: 6 }} /> : null}
          {vinculando ? "Vinculando..." : "Vincular nota(s)"}
        </Button>
      </div>
    </ModalHub>
  );
}
